import { Github } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Button } from '@reflex/ui/components/ui/button';
import { useAuth } from '@reflex/ui/client/context';
import { useGitHubAppInstallation } from './useGitHubApps';
import { InstallGitHubAppButton } from './InstallGitHubAppButton';

export interface GitHubAppRequiredDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  /**
   * Completes the sentence "This organization needs the GitHub App installed
   * to {reason}." e.g. "publish this automation", "build from your organization's
   * repositories". Keep it a lowercase verb phrase.
   */
  reason: string;
}

/**
 * The one reusable prompt for "this needs the org GitHub App, which is not
 * installed." Every surface that requires the installation — the flow-publish
 * gate, the blueprint repo picker — opens this instead of re-implementing the
 * install CTA and the "no App registered" fallback. It reads the org's
 * installation state from {@link useGitHubAppInstallation} and reuses
 * {@link InstallGitHubAppButton} for the CSRF-bound install flow.
 */
export function GitHubAppRequiredDialog({
  open,
  onOpenChange,
  reason,
}: GitHubAppRequiredDialogProps) {
  const { activeMembership } = useAuth();
  const organizationId = activeMembership?.organization.id ?? null;
  const { appRegistered, defaultApp, isLoading, isError } = useGitHubAppInstallation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="github-app-required-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="size-4" /> GitHub App required
          </DialogTitle>
          <DialogDescription>
            This organization needs the GitHub App installed to {reason}. It authenticates webhook
            delivery and repository access for the whole organization.
          </DialogDescription>
        </DialogHeader>

        {/* Defensive against a future caller that opens this during load/error:
            don't show the "no App registered" remedy until we actually know. */}
        {isLoading || isError ? (
          <p className="text-sm text-muted-foreground" data-testid="github-app-checking">
            {isError
              ? 'Unable to check the GitHub App installation. Try again in a moment.'
              : 'Checking the GitHub App installation…'}
          </p>
        ) : appRegistered && defaultApp ? (
          <div className="flex flex-col gap-2">
            <InstallGitHubAppButton
              app={defaultApp}
              organizationId={organizationId}
              variant="primary"
              onInstallStarted={() => onOpenChange(false)}
            />
            <p className="text-xs text-muted-foreground">
              Installation opens on GitHub in a new tab. Retry once it finishes.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="github-app-not-registered">
            No GitHub App is configured for this deployment yet. Ask an administrator to set it up
            under Settings → Integrations → GitHub.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="github-app-required-dismiss"
          >
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
